import { type Department, type ListDepartmentsQuery } from '@clockwise/clients/departments';
import { ApiError } from '@clockwise/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Download,
  Filter,
  Pencil,
  Plus,
  Trash2,
  Undo2,
  Upload,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { CatalogFormSheet } from '@/components/custom/catalog-form-sheet';
import { DepartmentImportSheet } from '@/components/custom/department-import-sheet';
import { RequirePermission } from '@/components/custom/require-permission';
import { RowActions } from '@/components/custom/row-actions';
import { SearchInput } from '@/components/custom/search-input';
import { StickyTableScroll } from '@/components/custom/sticky-table-scroll';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { departmentsApi } from '@/lib/api';
import { triggerDownload } from '@/lib/download';
import { queryKeys } from '@/lib/query-keys';

const SORT_FIELD_VALUES = ['name', 'code', 'createdAt', 'updatedAt'] as const;
type SortField = (typeof SORT_FIELD_VALUES)[number];

const listSearch = z.object({
  q: z.string().optional(),
  status: z.enum(['active', 'inactive', 'all']).optional(),
  page: z.coerce.number().int().min(0).optional(),
  sortField: z.enum(SORT_FIELD_VALUES).optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
});
type ListSearch = z.infer<typeof listSearch>;

export const Route = createFileRoute('/_authenticated/setup/departments/')({
  validateSearch: listSearch,
  component: DepartmentsPage,
});

const PAGE_SIZE = 25;
const DEFAULT_SORT_FIELD: SortField = 'name';

function DepartmentsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const sortField: SortField = search.sortField ?? DEFAULT_SORT_FIELD;
  const sortDirection: 'asc' | 'desc' = search.sortDirection ?? 'asc';
  const page = search.page ?? 0;
  const q = search.q ?? '';
  const status = search.status ?? 'active';

  const sorting: SortingState = [{ id: sortField, desc: sortDirection === 'desc' }];

  const updateSearch = (next: Partial<ListSearch>) =>
    navigate({
      search: (prev: ListSearch) => stripDefaults({ ...prev, ...next }),
      replace: true,
    });

  // One sheet serves both add and edit — `editing` null means create.
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<Department | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const queryClient = useQueryClient();

  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => {
    setSearchInput(q);
  }, [q]);
  useEffect(() => {
    if (searchInput === q) return;
    const handle = setTimeout(() => updateSearch({ q: searchInput, page: 0 }), 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, q]);

  // Export mirrors the on-screen filters exactly — what the user sees
  // is what they get in the file. Reads `searchInput` (the typed
  // value), NOT URL-state `q`, because the search box debounces by
  // 300ms before committing to the URL. Clicking Export within that
  // window otherwise exports the previous query instead of what's
  // currently typed.
  const exportMutation = useMutation({
    mutationFn: () =>
      departmentsApi.exportXlsx({
        active: status === 'all' ? undefined : status === 'active',
        q: searchInput.trim() || undefined,
        sortField,
        sortDirection,
      }),
    onSuccess: ({ blob, filename }) => {
      triggerDownload(blob, filename ?? 'departments.xlsx');
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Export failed';
      toast.error(msg);
    },
  });

  const listQuery: ListDepartmentsQuery = {
    q: q.trim() || undefined,
    active: status === 'all' ? undefined : status === 'active',
    sortField,
    sortDirection,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: queryKeys.departments.list(listQuery),
    queryFn: () => departmentsApi.list(listQuery),
    placeholderData: keepPreviousData,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => departmentsApi.remove(id),
    onSuccess: () => {
      toast.success('Department deactivated');
      queryClient.invalidateQueries({ queryKey: queryKeys.departments.all });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Deactivate failed'),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => departmentsApi.restore(id),
    onSuccess: () => {
      toast.success('Department reactivated');
      queryClient.invalidateQueries({ queryKey: queryKeys.departments.all });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Restore failed'),
  });

  const columns = useMemo<ColumnDef<Department>[]>(
    () => [
      {
        accessorKey: 'code',
        header: 'Code',
        cell: ({ row }) => (
          <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] uppercase text-muted-foreground">
            {row.original.code}
          </span>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <Link
            to="/setup/departments/$id"
            params={{ id: row.original.id }}
            className="font-medium text-primary underline-offset-2 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {row.original.name}
            {!row.original.active ? (
              <span className="ml-2 text-xs text-muted-foreground">(inactive)</span>
            ) : null}
          </Link>
        ),
      },
      {
        accessorKey: 'updatedAt',
        header: 'Updated',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {new Date(row.original.updatedAt).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <RequirePermission permission="department.edit">
            <RowActions>
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label="Edit"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditing(row.original);
                  setSheetOpen(true);
                }}
              >
                <Pencil />
              </Button>
              {row.original.active ? (
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label="Deactivate"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`Deactivate "${row.original.name}"?`)) {
                      deleteMutation.mutate(row.original.id);
                    }
                  }}
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 />
                </Button>
              ) : (
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label="Reactivate"
                  onClick={(e) => {
                    e.stopPropagation();
                    restoreMutation.mutate(row.original.id);
                  }}
                  disabled={restoreMutation.isPending}
                >
                  <Undo2 />
                </Button>
              )}
            </RowActions>
          </RequirePermission>
        ),
      },
    ],
    [deleteMutation, restoreMutation],
  );

  const table = useReactTable({
    data: query.data?.data ?? [],
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const first = next[0];
      if (!first) {
        updateSearch({ sortField: undefined, sortDirection: undefined });
        return;
      }
      updateSearch({
        sortField: first.id as SortField,
        sortDirection: first.desc ? 'desc' : 'asc',
        page: 0,
      });
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    pageCount: Math.max(1, Math.ceil((query.data?.meta.total ?? 0) / PAGE_SIZE)),
  });

  const total = query.data?.meta.total ?? 0;
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min(total, page * PAGE_SIZE + (query.data?.data.length ?? 0));

  return (
    <PageShell>
      <PageHeader
        title="Departments"
        subtitle="Org groupings for your team (Engineering, Finance, …)."
        count={total}
      >
        <RequirePermission permission="department.view">
          <Button
            variant="outline"
            onClick={() => exportMutation.mutate()}
            disabled={exportMutation.isPending}
          >
            <Download />
            {exportMutation.isPending ? 'Exporting…' : 'Export'}
          </Button>
        </RequirePermission>
        <RequirePermission permission="department.edit">
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload />
            Import
          </Button>
        </RequirePermission>
        <RequirePermission permission="department.edit">
          <Button
            onClick={() => {
              setEditing(null);
              setSheetOpen(true);
            }}
          >
            <Plus />
            Add department
          </Button>
        </RequirePermission>
      </PageHeader>

      <div
        data-surface="panel"
        className="flex flex-wrap items-center gap-3 rounded-md border bg-card p-6 py-4"
      >
        <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
          <Filter className="size-3.5" />
          Filters:
        </span>
        <SearchInput
          placeholder="Search code, name…"
          value={searchInput}
          onValueChange={setSearchInput}
        />
        <Select
          value={status}
          onValueChange={(v) => updateSearch({ status: v as ListSearch['status'], page: 0 })}
        >
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <StickyTableScroll>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((h) => {
                  const sortable = h.column.getCanSort();
                  const dir = h.column.getIsSorted();
                  return (
                    <TableHead key={h.id}>
                      {sortable ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 select-none hover:text-foreground"
                          onClick={h.column.getToggleSortingHandler()}
                        >
                          {flexRender(h.column.columnDef.header, h.getContext())}
                          {dir === 'asc' ? (
                            <ArrowUp className="size-3" />
                          ) : dir === 'desc' ? (
                            <ArrowDown className="size-3" />
                          ) : (
                            <ArrowUpDown className="size-3 opacity-40" />
                          )}
                        </button>
                      ) : (
                        flexRender(h.column.columnDef.header, h.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {columns.map((_c, j) => (
                    <TableCell key={`skeleton-cell-${i}-${j}`}>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : query.isError ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="py-10 text-center text-destructive">
                  {query.error instanceof ApiError
                    ? query.error.message
                    : 'Failed to load departments.'}
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="py-10 text-center text-muted-foreground"
                >
                  {q
                    ? 'No departments match this search.'
                    : 'No departments yet. Click “Add department”.'}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((r) => (
                <TableRow
                  key={r.id}
                  className="cursor-pointer"
                  onClick={() =>
                    navigate({
                      to: '/setup/departments/$id',
                      params: { id: r.original.id },
                    })
                  }
                >
                  {r.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell ?? defaultCell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StickyTableScroll>

      <footer className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {total === 0 ? 'No results' : `Showing ${showingFrom}–${showingTo} of ${total}`}
        </span>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => updateSearch({ page: Math.max(0, page - 1) })}
            disabled={page === 0 || query.isFetching}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => updateSearch({ page: page + 1 })}
            disabled={(page + 1) * PAGE_SIZE >= total || query.isFetching}
          >
            Next
          </Button>
        </div>
      </footer>

      <CatalogFormSheet
        open={sheetOpen}
        onOpenChange={(o) => {
          setSheetOpen(o);
          if (!o) setEditing(null);
        }}
        entityLabel="department"
        entity={editing}
        codeField={{ label: 'Code', placeholder: 'ENG' }}
        // Departments don't have a description field — the API doesn't
        // accept one and the form would otherwise render an empty box.
        showDescription={false}
        submit={(body) => {
          if (!body.code) {
            throw new Error('code missing — CatalogFormSheet contract violation');
          }
          return editing
            ? departmentsApi.patch(editing.id, { name: body.name, code: body.code })
            : departmentsApi.create({ name: body.name, code: body.code });
        }}
        invalidateKeys={
          editing
            ? [
                queryKeys.departments.all,
                queryKeys.audit.all,
                queryKeys.departments.detail(editing.id),
              ]
            : [queryKeys.departments.all, queryKeys.audit.all]
        }
      />

      <DepartmentImportSheet open={importOpen} onOpenChange={setImportOpen} />
    </PageShell>
  );
}

const defaultCell: ColumnDef<Department>['cell'] = ({ getValue }) => String(getValue() ?? '');

function stripDefaults(s: ListSearch): ListSearch {
  const out: ListSearch = {};
  if (s.q && s.q.length > 0) out.q = s.q;
  if (s.status && s.status !== 'active') out.status = s.status;
  if (s.page && s.page > 0) out.page = s.page;
  if (s.sortField && s.sortField !== DEFAULT_SORT_FIELD) out.sortField = s.sortField;
  if (s.sortDirection && s.sortDirection !== 'asc') out.sortDirection = s.sortDirection;
  return out;
}
